import { type FC, useCallback, useState } from "react";
import {
  Box,
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  HiddenFormLabel,
  NumberInput,
  Row,
  type RowProps,
  Text,
} from "@hightouch/htui";

import { commaNumber } from "src/utils/numbers";

export const BasicPagination: FC<
  Readonly<
    {
      setPage: (page: number) => void;
      page: number;
      disableNextPage: boolean;
      showPageInput?: boolean;
      /**
       * Informational only: `disableNextPage` stays the authority on whether a
       * next page exists.
       */
      pages?: number;
    } & RowProps
  >
> = ({
  setPage,
  page,
  disableNextPage,
  showPageInput = false,
  pages,
  ...props
}) => {
  // Remounts the input when a commit leaves the page unchanged (cleared or
  // re-entered value), so it snaps back to showing the current page.
  const [inputResetKey, setInputResetKey] = useState(0);

  const next = useCallback(() => {
    setPage(page + 1);
  }, [setPage, page]);

  const prev = useCallback(() => {
    setPage(page - 1);
  }, [setPage, page]);

  if (disableNextPage && page === 0) {
    return null;
  }

  return (
    <Row alignSelf="flex-end" alignItems="center" gap={2} mt={4} {...props}>
      <Button
        isDisabled={page === 0}
        icon={ChevronLeftIcon}
        size="sm"
        variant="secondary"
        onClick={() => prev()}
      >
        Previous
      </Button>
      {showPageInput && (
        <>
          <HiddenFormLabel label="Page number">
            <Box width={16}>
              <NumberInput
                key={`${page}-${inputResetKey}`}
                size="sm"
                width="100%"
                min={1}
                max={pages}
                formatOptions={{ maximumFractionDigits: 0 }}
                value={page + 1}
                onChange={(value) => {
                  if (value === undefined || value - 1 === page) {
                    setInputResetKey((key) => key + 1);
                    return;
                  }
                  setPage(value - 1);
                }}
              />
            </Box>
          </HiddenFormLabel>
          {pages !== undefined && (
            <Text color="text.secondary" whiteSpace="nowrap">
              of {commaNumber(pages)}
            </Text>
          )}
        </>
      )}
      <Button
        isDisabled={disableNextPage}
        directionIcon={ChevronRightIcon}
        size="sm"
        variant="secondary"
        onClick={() => next()}
      >
        Next
      </Button>
    </Row>
  );
};
